/**
 * Create Table Store
 *
 * Zustand store for managing the create table sheet wizard state.
 * Handles all state across the 3 steps: Upload, Configure, Review.
 *
 * Features:
 * - Step navigation with validation
 * - File and form data management
 * - Processing state tracking
 * - Error handling
 * - Reset functionality
 *
 * @module features/workspace/stores/useCreateTableStore
 */

import { create } from 'zustand';
import type {
  CreateTableStep,
  CreateTableFieldConfig,
  CreateTableSheetState,
} from '@loader/shared-types';
import { VERSION_IN_PROGRESS_STATUSES } from '@loader/shared-types';

import { useSidePanelStore } from './useSidePanelStore';

import { MAX_PROCESSING_STALENESS_MS } from '@/lib/constants';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Data needed to resume a draft table
 */
export interface DraftTableData {
  tableId: string;
  tableName: string;
  folderId: string | null;
  versionId: string | null;
  versionStatus: CreateTableSheetState['versionStatus'];
  fields: CreateTableFieldConfig[];
  /** Original file name */
  fileName: string | null;
  /** Original file size in bytes */
  fileSize: number | null;
  /** Version created_at ISO string (for staleness check) */
  versionCreatedAt: string | null;
}

/**
 * Actions available on the create table store
 */
interface CreateTableStoreActions {
  // Sheet controls
  /** Open the create table sheet */
  openSheet: (folderId?: string | null) => void;
  /** Open the sheet to resume a draft table */
  resumeDraft: (data: DraftTableData) => void;
  /** Close the sheet and reset state */
  closeSheet: () => void;

  // Step navigation
  /** Go to a specific step */
  goToStep: (step: CreateTableStep) => void;
  /** Go to the next step */
  nextStep: () => void;
  /** Go to the previous step */
  previousStep: () => void;

  // Step 1: Upload actions
  /** Set the table name */
  setTableName: (name: string) => void;
  /** Set whether name exists (duplicate check) */
  setNameExists: (exists: boolean) => void;
  /** Set the selected file */
  setFile: (file: File | null) => void;
  /** Set the Excel sheet name to import (for multi-sheet workbooks) */
  setSheetName: (sheetName: string | null) => void;

  // Step 2: Configure actions
  /** Set working table data after upload */
  setWorkingTableData: (data: {
    workingTableId: string;
    versionId: string;
    versionStatus: NonNullable<CreateTableSheetState['versionStatus']>;
    fields: CreateTableFieldConfig[];
    fileName?: string;
    fileSize?: number;
  }) => void;
  /** Update version status (for polling) */
  setVersionStatus: (status: NonNullable<CreateTableSheetState['versionStatus']>) => void;
  /** Update a single field configuration */
  updateField: (fieldId: string, updates: Partial<CreateTableFieldConfig>) => void;
  /** Update all fields at once */
  setFields: (fields: CreateTableFieldConfig[]) => void;

  // Processing state
  /** Set extracting state (step 1 -> 2 transition) */
  setIsExtracting: (isExtracting: boolean) => void;
  /** Set saving fields state (step 2 field updates) */
  setIsSavingFields: (isSaving: boolean) => void;
  /** Set finalizing state (step 3 create action) */
  setIsFinalizing: (isFinalizing: boolean) => void;
  /** Set error message */
  setError: (message: string | null) => void;
  /** Set upload progress (0-100) */
  setUploadProgress: (progress: number) => void;

  // Reset
  /** Reset all state to initial values */
  reset: () => void;
}

/**
 * Combined store type
 */
type CreateTableStore = CreateTableSheetState & CreateTableStoreActions;

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: CreateTableSheetState = {
  currentStep: 'upload',
  isOpen: false,
  folderId: null,
  isResumingDraft: false,

  // Step 1: Upload
  tableName: '',
  file: null,
  fileName: null,
  fileSize: null,
  nameExists: false,
  sheetName: null,

  // Step 2: Configure
  workingTableId: null,
  versionId: null,
  versionStatus: null,
  fields: [],

  // Processing state
  isExtracting: false,
  isSavingFields: false,
  isFinalizing: false,
  errorMessage: null,
  uploadProgress: 0,
};

// ============================================================================
// STEP ORDER
// ============================================================================

/** Ordered steps for the create table wizard */
export const STEP_ORDER: CreateTableStep[] = ['upload', 'configure', 'review'];

/**
 * Get the next step in the wizard
 */
function getNextStep(current: CreateTableStep): CreateTableStep | null {
  const currentIndex = STEP_ORDER.indexOf(current);
  if (currentIndex < STEP_ORDER.length - 1) {
    return STEP_ORDER[currentIndex + 1];
  }
  return null;
}

/**
 * Get the previous step in the wizard
 */
function getPreviousStep(current: CreateTableStep): CreateTableStep | null {
  const currentIndex = STEP_ORDER.indexOf(current);
  if (currentIndex > 0) {
    return STEP_ORDER[currentIndex - 1];
  }
  return null;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

/**
 * Create table store
 *
 * Manages state for the 3-step create table wizard:
 * 1. Upload: Name + file selection
 * 2. Configure: Field customization
 * 3. Review: Summary and finalization
 */
export const useCreateTableStore = create<CreateTableStore>()(
  (set, get) => ({
    ...initialState,

    // ============================================================================
    // SHEET CONTROLS
    // ============================================================================

    openSheet: (folderId = null) => {
      // Always reset to initial state for a fresh create-table flow.
      set({
        ...initialState,
        isOpen: true,
        folderId,
      });

      // Also open the side panel in createTable mode
      useSidePanelStore.getState().openCreateTable();
    },

    resumeDraft: (data: DraftTableData) => {
      // Determine which step to start on:
      // - If version is genuinely uploading (in-progress and not stale) → upload step to show progress
      // - Otherwise → configure step to setup fields
      const isInProgress =
        data.versionStatus && VERSION_IN_PROGRESS_STATUSES.includes(data.versionStatus);
      const isStale = data.versionCreatedAt
        ? Date.now() - new Date(data.versionCreatedAt).getTime() > MAX_PROCESSING_STALENESS_MS
        : false;
      const isUploading = isInProgress && !isStale;
      const startStep: CreateTableStep = isUploading ? 'upload' : 'configure';

      set({
        ...initialState,
        isOpen: true,
        isResumingDraft: true,
        currentStep: startStep,
        folderId: data.folderId,
        tableName: data.tableName,
        workingTableId: data.tableId,
        versionId: data.versionId,
        versionStatus: data.versionStatus,
        fields: data.fields,
        fileName: data.fileName,
        fileSize: data.fileSize,
      });
      // Also open the side panel in createTable mode
      useSidePanelStore.getState().openCreateTable();
    },

    closeSheet: () => {
      set(initialState);
      useSidePanelStore.getState().close();
    },

    // ============================================================================
    // STEP NAVIGATION
    // ============================================================================

    goToStep: (step) => {
      set({ currentStep: step, errorMessage: null });
    },

    nextStep: () => {
      const nextStep = getNextStep(get().currentStep);
      if (nextStep) {
        set({ currentStep: nextStep, errorMessage: null });
      }
    },

    previousStep: () => {
      const { currentStep, isResumingDraft } = get();
      const prevStep = getPreviousStep(currentStep);

      // When resuming a draft, don't allow going back to upload step
      if (prevStep && !(isResumingDraft && prevStep === 'upload')) {
        set({ currentStep: prevStep, errorMessage: null });
      }
    },

    // ============================================================================
    // STEP 1: UPLOAD ACTIONS
    // ============================================================================

    setTableName: (tableName) => {
      set({ tableName, errorMessage: null });
    },

    setNameExists: (nameExists) => {
      set({ nameExists });
    },

    setFile: (file) => {
      set({ file, errorMessage: null });
    },

    setSheetName: (sheetName) => {
      set({ sheetName, errorMessage: null });
    },

    // ============================================================================
    // STEP 2: CONFIGURE ACTIONS
    // ============================================================================

    setWorkingTableData: (data) => {
      set({
        workingTableId: data.workingTableId,
        versionId: data.versionId,
        versionStatus: data.versionStatus,
        fields: data.fields,
        ...(data.fileName && { fileName: data.fileName }),
        ...(data.fileSize && { fileSize: data.fileSize }),
      });
    },

    setVersionStatus: (versionStatus) => {
      set({ versionStatus });
    },

    updateField: (fieldId, updates) => {
      set((state) => ({
        fields: state.fields.map((field) =>
          field.id === fieldId ? { ...field, ...updates } : field
        ),
      }));
    },

    setFields: (fields) => {
      set({ fields });
    },

    // ============================================================================
    // PROCESSING STATE
    // ============================================================================

    setIsExtracting: (isExtracting) => {
      set({ isExtracting });
    },

    setIsSavingFields: (isSavingFields) => {
      set({ isSavingFields });
    },

    setIsFinalizing: (isFinalizing) => {
      set({ isFinalizing });
    },

    setError: (errorMessage) => {
      set({ errorMessage });
    },

    setUploadProgress: (uploadProgress) => {
      set({ uploadProgress });
    },

    // ============================================================================
    // RESET
    // ============================================================================

    reset: () => {
      set(initialState);
    },
  })
);

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

/**
 * Check if the user can go back from the current step
 * When resuming a draft, can't go back to upload step
 */
export function useCanGoBack(): boolean {
  const { currentStep, isResumingDraft } = useCreateTableStore();
  const prevStep = getPreviousStep(currentStep);

  if (!prevStep) return false;
  if (isResumingDraft && prevStep === 'upload') return false;
  return true;
}
